<template>
    <div class="mt-3">
        <div class="d-flex align-items-center">
            <div class="me-4">
                <label class="me-4">
                    <input type="radio" name="option" v-model="input" :value="Option.Number" class="me-1">
                    {{ $t('form.labels.exactNumber') }}
                </label>
                <label>
                    <input type="radio" name="option" v-model="input" :value="Option.Range" class="me-1">
                  {{ $t('form.labels.numberRange') }}
                </label>
            </div>
            <div v-if="input === Option.Number">
                <input v-model="numberValueRaw" type="number" class="form-control">
            </div>
            <div v-if="input === Option.Range">
                <div class="d-flex align-items-center">
                    <input v-model="minRaw" type="number" class="form-control">
                    <span class="ms-2 me-2">-</span>
                    <input v-model="maxRaw" type="number" class="form-control">
                </div>
            </div>
        </div>
        <div class="border p-3 d-flex rounded mt-3">
            <div>{{ bounds[0]}}</div>
            <div class="flex-grow-1 ps-4 pe-4">
                <VueSlider v-if="input === Option.Number" :min="bounds[0]" :max="bounds[1]"
                           :modelValue="normalize(numberValue)" @change="numberValueRaw = $event"/>

                <VueSlider v-if="input === Option.Range"
                           :min="bounds[0]" :max="bounds[1]"
                           :modelValue="[normalize(min), normalize(max)]"
                           @change="updateRangeSlider"/>
            </div>

            <div>{{ bounds[1]}}</div>
        </div>
        <div v-if="isInvalid()" class="invalid-feedback d-block">
            <template v-if="! isValueInBounds()">{{ $t('form.errors.notANumber') }}</template>
            <template v-else-if="getValue() === null">{{ $t('form.errors.parameterNotFilled', [label]) }}</template>
        </div>
    </div>
</template>

<script lang="ts">
import {Control} from '@/composables/Control';
import {computed, defineComponent, onMounted, PropType, ref, watch} from 'vue';
import VueSlider from 'vue-slider-component';

export enum Option {
    Number,
    Range,
}

type Value = number | [number, number];

export default defineComponent({
    components: {VueSlider},
    props: {
        value: {
            type: [Number, Array] as PropType<Value | null>,
            default: null,
        },
        validate: {
            type: Boolean,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        bounds: {
            type: Array as PropType<number[]>,
            required: true,
        },
    },
    emits: ['input', 'validated'],
    setup(props, {emit}) {
        const input = ref<Option>(Option.Number);
        const numberValueRaw = ref('');
        const minRaw = ref('');
        const maxRaw = ref('');

        function getInt(value: string): number | null {
            const converted = Number.parseInt(value, 10);

            return isNaN(converted) ? null : converted;
        }

        const min = computed((): number | null => {
            return getInt(minRaw.value);
        });

        const max = computed((): number | null => {
            return getInt(maxRaw.value);
        });

        const numberValue = computed((): number | null => {
            return getInt(numberValueRaw.value);
        });

        function getValue(): Value | null {
            if (input.value === Option.Number) {
                return numberValue.value;
            }

            if (input.value === Option.Range && min.value !== null && max.value !== null) {
                return [min.value, max.value];
            }

            return null;
        }

        function isInBounds(value: number): boolean {
            return value >= props.bounds[0] && value <= props.bounds[1];
        }

        function isValueInBounds(): boolean {
            const value = getValue();

            if (value === null) {
                return false;
            }

            return Array.isArray(value)
                ? isInBounds(value[0]) && isInBounds(value[1])
                : isInBounds(value);
        }

        function validateCurrentValue(): boolean {
            const value = getValue();

            if (value === null) {
                return false;
            }

            return isValueInBounds();
        }

        const {updateValue, isInvalid} = Control<Value>(props, emit, {
            getValue,
            validateCurrentValue,
        });

        // mounted logic
        onMounted(() => {
            if (Array.isArray(props.value)) {
                minRaw.value = '' + props.value[0];
                maxRaw.value = '' + props.value[1];
                input.value = Option.Range;
                return;
            }

            if (props.value !== null) {
                numberValueRaw.value = '' + props.value;
                input.value = Option.Number;
                return;
            }

            input.value = Option.Number;
        });

        watch(numberValueRaw, () => {
            updateValue();
        });

        watch(minRaw, () => {
            updateValue();
        });

        watch(maxRaw, () => {
            updateValue();
        });

        function updateRangeSlider(value: [string, string]): void {
            minRaw.value = value[0];
            maxRaw.value = value[1];
        }

        function normalize(value: number): number {
            return Math.max(props.bounds[0], Math.min(props.bounds[1], value));
        }

        return {
            Option,
            input,
            numberValueRaw,
            minRaw,
            maxRaw,
            min,
            max,
            numberValue,
            getValue,
            isValueInBounds,
            updateRangeSlider,
            normalize,
            isInvalid,
        };
    },
});
</script>
